import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { endOfDay, isSameDay } from 'date-fns';
import { IOperatorsDAO, OperatorRole } from '../../../../api/operators.dao';
import { getMeetingGroupsWithHiddenSection, getOrganizationsForSpeakersSync, getProgressList, groupListByTime } from '../../../../infrastructure/utils/common.utils';
import { meetingMatchesSearch } from '../../../../infrastructure/utils/meetings-filtering.utils';
import { utcToZonedTime, zonedTimeToUtc } from '../../../../infrastructure/utils/date-fns-overrides.utils';
import { CLONED_CONNECTION, MINIMAL_SEARCH_LENGTH, SCROLL_HEADER_HEIGHT } from '../../../../constants/common.constants';
import { IApiDateConverter } from '../../../../infrastructure/api-date-converter';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { ILocalStorageService } from '../../../../infrastructure/local-storage-service';
import { SortOrder } from '../../../../api/shared.dto';
import { IEventDetailsDTO, IFilterValue, IOnSyncEventSubscriptionResponse, ISelectedFilter, OnSyncEventType } from '../../../../api/events.dao';
import {
    CONTROL_FILTER_VIEW_TAB,
    IControlDAO,
    IControlEventMeetingsDTO,
    IControlMeetingDetailsDTO,
    IControlRoomDTO,
    IGetControlMeetingsRequestParams,
    IMeetingAttendeeLiveData,
    IUpdateMeetingAttendeeLiveDataResponse,
    IUpdateMeetingSubscriptionResponse,
    IUpdateRoomSubscriptionResponse,
    TCopyPodiumLinkFrom,
} from '../../../../api/control.dao';
import { debounce } from '../../../../infrastructure/utils/debounce.utils';
import { onPopulateAggregatedAttendeeLiveCounters, onSyncEvent, onUpdateMeetingControl, onUpdateRoom } from '../../../../graphql/subscriptions';
import { ISubWindowFormMethods } from '../manage-event.viewmodel';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { CONTROL_PAGE_SIZE_NAME } from '../../../../constants/pagination.contants';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../../api/connections.dao';
import { IAttendeeDetailsDTO, IAttendeeOrganizationDTO, MeetingAttendeeStatus } from '../../../../api/attendees.dao';
import { IOperatorDetailsDTO } from '../../../../api/rooms.dao';
import {
    MeetingStatus,
    TMeetingsFilterItem,
    TSavedMeetingsFilterItem,
    TSelectedFilterChaperone,
    TSelectedFilterOperators,
    TSelectedFilterStatuses,
    TSelectedFilterTypes
} from '../../../../api/meetings.dao';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../infrastructure/utils/appsync.utils';
import { IGNORE_ATTENDEES, IOrganizationsDAO } from '../../../../api/organizations.dao';
import { CONTROL_TAB_SELECTED_FILTERS, MeetingProgressLabel, MeetingProgressList } from '../../../../constants/meeting.constants';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import { ZoomMeetingVM } from '../shared/connections-list/_zoom-meeting-modal/zoom-meeting.viewmodel';
import { WebinarReportsViewModel } from '../shared/webinar-report/webinar-reports.viewmodel';
import { ControlFormVM } from './_control-form/_control-form.viewmodel';
import { ControlForm } from './_control-form/_control-form.presentation';
import {
    ATTENDEE_INFO_POPOVER_TRACKING_EVENTS,
    CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS,
    CONTROL_PAGE_TRACKING_EVENTS,
    convertBoolTypeForMixPanel,
    OpenFrom,
    TRACKING_EVENTS
} from '../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { getComposedConnectionValues } from '../../../../infrastructure/utils/connections.utils';
import { AttendeesLiveForm } from './_attendees-live-form/attendees-live-form.container';
import { IControlListItemProps } from './_control-list/_control-list-item/_control-list-item';
import {
    EventDataSyncChangeset,
    MeetingChangesetMapper,
    MeetingDataSyncChangeset,
    MeetingRoomDataSyncChangeset
} from '../../../../modules/event-sync-updates/meeting-changeset.mapper';
import { IArtifactsDAO } from '../../../../api/artifacts.dao';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { CreateZoomWebinarVM } from '../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar.viewmodel';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../api/jobs.dao';
import { IDeleteConnectionDetails } from '../shared/connections-list/_connection-delete-confirm-popover';
import type { ConnectionsViewModel } from '../_connections/connections.viewmodel';
import { TYPE } from '../../../../constants/common-labels.constants';
import { IConfigurePassportDisplayFormProps } from '../shared/configure-passport-display/configure-passport-display.viewmodel';
import { PARTICIPANTS_TEXT } from './control.texts';
import { AttendeeColumnPopoverViewmodel } from '../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';
import { IReportGenerationNotificationProps } from '../_reports/show-report-toast/show-report-toast';
import { IPodiumDAO } from '../../../../api/podium.dao';
import { MeetingApiParams } from '../shared/connections-list/_connection-form/connection-form.viewmodel';

export enum SelectionAction {
    Previous = 'Previous',
    Next = 'Next',
    Now = 'Now'
}
interface IMeetingTimeGroup {
    startDateTime: string;
    endDateTime: string;
}
const OPERATOR_ROLES = [...Object.values(OperatorRole), null];

class ControlViewModel {
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    public meetingGroupRefs: { startDateTime: Date; endDateTime: Date; ref: React.RefObject<HTMLElement> }[] = [];
    @mobx.observable
    private _meetingsList: IControlMeetingDetailsDTO[] = [];
    private zoomMeetingVM: ZoomMeetingVM;
    private webinarMeetingVM: CreateZoomWebinarVM;
    private webinarReportsVM: WebinarReportsViewModel;
    @mobx.observable
    public groupedOperatorsMap: { [meetingId: number]: { [path: string]: IOperatorDetailsDTO[] } } = {};
    @mobx.observable
    private meetingIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    private attendeesLiveIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    private isDownloadEventFormShown: boolean;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    public filterDays: Date[] = [];
    @mobx.observable
    public isMeetingsDatesLoading: boolean = false;
    @mobx.observable
    public selectedFilterDays: Date[] = [];
    @mobx.observable
    public firstSelectedDate: Date | null = null;
    @mobx.observable
    public lastSelectedDate: Date | null = null;
    @mobx.observable
    public selectedFilterOperators = '';
    @mobx.observable
    public selectedSortOrder: SortOrder = SortOrder.Asc;
    @mobx.observable
    public selectedChaperone = '';
    @mobx.observable
    public selectedFilterStatuses = '';
    @mobx.observable
    public selectedFilterTypes = '';
    @mobx.observable
    public selectedFilterDayInEventTimezone: Date | null = null;
    @mobx.observable
    public collapsedMeetingIds: number[] = [];
    @mobx.observable
    public collapsedGroups: IMeetingTimeGroup[] = [];
    @mobx.observable
    public collapsedHiddenGroups: IMeetingTimeGroup[] = [];
    @mobx.observable
    public searchQuery = '';
    @mobx.observable
    private paginationVM: PagingViewModel;
    public meetingsForSync: number[] = [];
    public showEventNotesPopup: (isEditMode: boolean, openFrom?: OpenFrom) => void;
    public showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    public generateMeetingInteractionsReport: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => void;
    public generateMeetingRegistrationReport: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => Promise<void>;
    public fetchCustomFields: () => void;
    @mobx.observable
    public guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };
    private fetchMeetingsRequestId: number;
    public defaultColumns: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterColumns: IFilterValue[] = [];
    public localStorageFiltersName: string;
    private eventDataSubscription: IAppsyncSubscription;
    public handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void;
    public trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    @mobx.observable
    private connectionViewModel: ConnectionsViewModel;
    @mobx.observable
    private attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;

    private fetchMeetingsAbortController: AbortController | null = null;

    public constructor(
        private readonly accountId: number,
        private readonly _event: IEventDetailsDTO,
        private readonly intl: IntlShape,
        private readonly subWindowFormMethods: ISubWindowFormMethods,
        @IoC.Inject private readonly controlDAO: IControlDAO = IoC.Container.get(IControlDAO),
        @IoC.Inject private readonly localeStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
        @IoC.Inject private readonly operatorsDAO: IOperatorsDAO = IoC.Container.get(IOperatorsDAO),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly artifactsDAO: IArtifactsDAO = IoC.Container.get(IArtifactsDAO),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly podiumDao: IPodiumDAO = IoC.Container.get(IPodiumDAO),
    ) {
        this.meetingIdToSubWindowMap = {};
        this.attendeesLiveIdToSubWindowMap = {};
        this.fetchMeetingsRequestId = 1;
        this.event = _event;
        this.setUpPagination();
        this.initWebinarReportsVM();

        this.localStorageFiltersName = CONTROL_FILTER_VIEW_TAB.CONTROL;
        this.setDefaultColumns();
        this.loadFiltersFromStorage();

        this.executionRuntime.onComplete(JobType.EventBlueprintTrigger, async () => await this.fetchMeetingsRequest());
        this.initAttendeeDetailsPopoverVM();
    }

    public set meetingsList(value: IControlMeetingDetailsDTO[]) {
        const meetingsTimeGroups = groupListByTime<IControlMeetingDetailsDTO>(value);
        this.meetingGroupRefs = meetingsTimeGroups.map(([fistGroupMeeting]) =>
            ({
                startDateTime: fistGroupMeeting.startDateTime,
                endDateTime: fistGroupMeeting.endDateTime,
                ref: React.createRef()
            }));
        this._meetingsList = value;
    }

    public get meetingsList() {
        return this._meetingsList;
    }

    private get hasValidSearchQuery(): boolean {
        return !!this.searchQuery && this.searchQuery.trim().length >= MINIMAL_SEARCH_LENGTH;
    }

    public get filteredMeetingsList(): IControlMeetingDetailsDTO[] {
        if (this.hasValidSearchQuery) {
            return this.meetingsList?.filter(meeting => meetingMatchesSearch(meeting, this.searchQuery));
        }
        return this.meetingsList;
    }

    public get filteredMeetingGroupRefs(): { startDateTime: Date; endDateTime: Date; ref: React.RefObject<HTMLElement> }[] {
        if (!this.hasValidSearchQuery) {
            return this.meetingGroupRefs;
        }

        const filteredGroups = groupListByTime<IControlMeetingDetailsDTO>(this.filteredMeetingsList);

        // Reuse existing refs where possible, create new ones for new groups
        return filteredGroups.map(([firstGroupMeeting]) => {
            const existingRef = this.meetingGroupRefs.find(ref =>
                ref.startDateTime.getTime() === firstGroupMeeting.startDateTime.getTime()
            );

            return existingRef || {
                startDateTime: firstGroupMeeting.startDateTime,
                endDateTime: firstGroupMeeting.endDateTime,
                ref: React.createRef<HTMLElement>()
            };
        });
    }

    public setConnectionVMInstance = (instance: ConnectionsViewModel): void => {
        this.connectionViewModel = instance;
    };

    private static handleApiException(e: Error, title: string = 'API Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public didMount() {
        this.fetchCustomFields();
    }

    public fetchMeetingsRequest = async (trackFilters?: boolean, loadSavedFilters?: boolean) => {
        this.fetchMeetingsRequestId++;
        if (loadSavedFilters) {
            await this.loadSelectedFilters();
        }
        await this.fetchMeetings(this.fetchMeetingsRequestId);
        if (trackFilters) {
            this.trackFiltersEventMixPanel();
        }
    };

    public initZoomMeetingVM() {
        this.zoomMeetingVM = new ZoomMeetingVM(this.event.timeZone, null, new Date(),
            new Date(), null, this.accountId, this.event.eventId);
    }

    public initWebinarMeetingVM(): void {
        this.webinarMeetingVM = new CreateZoomWebinarVM(
            this.event.timeZone,
            '',
            new Date(),
            new Date(),
            null,
            { eventId: this.event.eventId, accountId: this.accountId }
        );
    }

    public getConnectionsVM(): ConnectionsViewModel {
        return this.connectionViewModel;
    }

    public getWebinarMeetingVM(): CreateZoomWebinarVM {
        return this.webinarMeetingVM;
    }

    public initWebinarReportsVM() {
        this.webinarReportsVM = new WebinarReportsViewModel(this.accountId, this.event.eventId, OpenFrom.Control);
    }

    public getAttendeeDetailsPopoverVM(): AttendeeColumnPopoverViewmodel {
        return this.attendeeDetailsPopoverVM;
    }

    public initAttendeeDetailsPopoverVM() {
        this.attendeeDetailsPopoverVM = new AttendeeColumnPopoverViewmodel(
            this.accountId,
            this.event.eventId,
            this.handleEditAttendeeClick,
            this.trackHandlerInAttendeePopover
        );
    }
    public setHandleTroubleshoot = (handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void): void => {
        this.handleTroubleshoot = handleTroubleshoot;
    };

    public setTrackHostJoinClick = (trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void): void => {
        this.trackHostJoinClick = trackHostJoinClick;
    };

    public handleCloneConnection = (connection: IConnectionDetailsDTO) => {
        const localeStorageData = {
            name: CLONED_CONNECTION,
            value: connection,
            creationDate: Date.now()
        };
        this.localeStorageService.setLocalStorageValue(localeStorageData);
        this.trackEventMixPanel(
            TRACKING_EVENTS.CONTROL_PAGE_CONNECTION_DETAILS_POPOVER,
            CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CLONE_CONNECTION
        );
    };

    public getZoomMeetingVM(): ZoomMeetingVM {
        return this.zoomMeetingVM;
    }

    public getWebinarReportsVM(): WebinarReportsViewModel {
        return this.webinarReportsVM;
    }

    public updateZoomMeetingCallback = async (callBack: (c: IConnectionDetailsDTO) => void, zoomMeetingDetails?: IConnectionDetailsDTO) => {
        await callBack(zoomMeetingDetails);
    };

    public updateWebinarMeetingCallback = async (callBack: (c: IConnectionDetailsDTO) => void, webinarMeetingDetails?: IConnectionDetailsDTO) => {
        await callBack(webinarMeetingDetails);
    };

    public cancelFetchMeetingsRequest = (): void => {
        if (this.fetchMeetingsAbortController && !this.fetchMeetingsAbortController.signal.aborted) {
            this.fetchMeetingsAbortController.abort();
        }
    };

    @mobx.action
    private fetchMeetings = async (requestId: number) => {
        try {
            this.loading = true;

            const completeParams: IGetControlMeetingsRequestParams = {
                eventId: this.event.eventId,
                accountId: this.accountId,
                timeZone: this.event.timeZone,
                ignoreAttendees: IGNORE_ATTENDEES,
                sortFields: [
                    {
                        order: this.selectedSortOrder,
                        field: 'startDateTime'
                    },
                    {
                        order: SortOrder.Desc,
                        field: 'status'
                    },
                    {
                        order: SortOrder.Desc,
                        field: 'organizations.role'
                    },
                    {
                        order: SortOrder.Asc,
                        field: 'organizations.organization.name'
                    },
                ],
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize()
            };

            if (this.selectedFilterOperators) {
                completeParams.operatorIds = this.selectedFilterOperators;
            }

            if (this.selectedChaperone) {
                // @ts-ignore
                completeParams.usernames = this.selectedChaperone;
            }

            if (this.selectedFilterTypes) {
                completeParams.types = this.selectedFilterTypes;
            }

            if (this.hasSelectedDateRange()) {
                completeParams.startDateTime = this.apiDateConverter.dateToAPIString(this.selectedFilterDays[0]);
                completeParams.endDateTime = this.apiDateConverter.dateToAPIString(this.selectedFilterDays[1]);
            }

            // cancel previous request if exists
            this.cancelFetchMeetingsRequest();
            this.fetchMeetingsAbortController = new AbortController();

            const meetingsListRequestResult = await this.controlDAO.getControlEventMeetingsList(completeParams, this.fetchMeetingsAbortController.signal);
            if (this.fetchMeetingsRequestId === requestId) {
                meetingsListRequestResult.content.forEach(meeting => this.setGroupedOperators(meeting));
                this.handleMeetingsLoaded(meetingsListRequestResult);
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            ControlViewModel.handleApiException(error);
        }
    };

    @mobx.action
    public applyFilters = async (sortOrder: SortOrder) => {
        this.selectedSortOrder = sortOrder;
        this.selectedFilterDays = this.convertSelectedDateRange([this.firstSelectedDate, this.lastSelectedDate]);
        this.saveSelectedFilters();
        await this.fetchMeetingsRequest(true);
    };

    @mobx.action
    public clearMeetings = () => {
        this.meetingsList = [];
    };

    @mobx.action
    public clearFilters = () => {
        this.searchQuery = '';
    };

    @mobx.action.bound
    public selectDates(dates: Date[]): void {
        const [ firstDate, lastDate ] = dates;

        this.firstSelectedDate = firstDate;

        if (isSameDay(firstDate,lastDate) || !lastDate) {
            this.lastSelectedDate = null;
        } else {
            this.lastSelectedDate = lastDate;
        }
    }

    public convertSelectedDateRange(selectedDates: (Date | null)[]): Date[] {
        if (!selectedDates || selectedDates.length === 0 || !selectedDates[0]) return [];

        const [startDate, endDate] = selectedDates;
        const selectedEndDate = endOfDay(endDate ?? startDate);

        return [
            zonedTimeToUtc(startDate, this.event.timeZone),
            zonedTimeToUtc(selectedEndDate, this.event.timeZone)
        ];
    }

    @mobx.action
    public clearFilteredDays = (): void => {
        this.firstSelectedDate = null;
        this.lastSelectedDate = null;
    };

    @mobx.action
    public fetchFilterHours = async (): Promise<void> => {
        this.isMeetingsDatesLoading = true;

        try {
            const response = await this.controlDAO.getMeetingStartHours({
                eventId: this.event.eventId,
                accountId: this.accountId
            });

            mobx.runInAction(() => {
                this.setFilterDays(response);
            });
        } catch (error) {
            ControlViewModel.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isMeetingsDatesLoading = false;
            });
        }
    };

    public getFilterDaysInEventTimezone = (days: (Date | null)[]): (Date | null)[] => {
        return days.map(hour => hour ? utcToZonedTime(hour, this.event.timeZone) : null);
    };

    @mobx.action
    public setFilterDays = (hours: Date[]) => {
        this.filterDays = this.getFilterDaysInEventTimezone(hours);
    };

    public hasSelectedDateRange(): boolean {
        return this.selectedFilterDays.length &&
            this.selectedFilterDays.every(day => day !== null);
    }

    public fetchOperators = async (): Promise<{ id: string; label: string }[]> => {
        const params = { accountId: this.accountId, eventId: this.event?.eventId };
        const response = await this.operatorsDAO.getOperators(params);

        return response.map(operator => {
            return {
                id: operator.operatorId.toString(),
                label: `${operator.firstName} ${operator.lastName}`,
            };
        });
    };


    public fetchChaperone = async (): Promise<{ id: string; label: string }[]> => {
        const params = { accountId: this.accountId, eventId: this.event?.eventId };
        const chaperones = await this.organizationDAO.getChaperones(params);
        return chaperones.map(chap => {
            return {
                id: chap.username,
                label: `${chap.firstName} ${chap.lastName}`,
            };
        });
    };

    public fetchMeetingTypes = async (): Promise<{ id: string; label: string }[]> => {
        const params = { accountId: this.accountId, eventId: this.event?.eventId };
        const response = await this.controlDAO.getMeetingTypes(params);
        const completeTypes = response.map(type => type?.trim()).filter(type => !!type);

        return completeTypes.map(type => {
            return {
                id: type,
                label: type,
            };
        });
    };

    public getMeetingStatuses = (): Promise<{ id: string; label: string }[]> => {
        return new Promise(resolve => {
            resolve(
                MeetingProgressList.map(status => {
                    return {
                        id: status,
                        label: MeetingProgressLabel[status]
                    };
                })
            );
        });
    };

    @mobx.action
    public handleChangeTypeFilter = async (types: string) => {
        this.selectedFilterTypes = types;
        this.saveSelectedFilters();
        await this.fetchMeetingsRequest(true);
    };

    @mobx.action
    public handleChangeOperatorsFilter = async (operatorIds: string) => {
        this.selectedFilterOperators = operatorIds;
        this.saveSelectedFilters();
        await this.fetchMeetingsRequest(true);
    };

    @mobx.action
    public handleChangeChaperoneFilter = async (chaperone: string) => {
        this.selectedChaperone = chaperone;
        this.saveSelectedFilters();
        await this.fetchMeetingsRequest(true);
    };

    @mobx.action
    public handleChangeStatusFilter = (statuses: string) => {
        this.selectedFilterStatuses = statuses;
        if (statuses.length) {
            this.collapseHiddenSections();
        } else {
            this.collapsedHiddenGroups = [];
        }
        this.saveSelectedFilters();
        this.trackFiltersEventMixPanel();
    };

    @mobx.action
    public collapseHiddenSections = () => {
        const progressList = getProgressList(this.selectedFilterStatuses);
        if (progressList.length) {
            const meetingGroups = getMeetingGroupsWithHiddenSection(this.meetingsList, progressList);

            this.collapsedHiddenGroups = meetingGroups.map(el => ({
                startDateTime: el.startDateTime.toString(),
                endDateTime: el.endDateTime.toString()
            }));
        }
    };

    @mobx.action
    public handleEditRoomClick = (roomId: number, path: OpenFrom = OpenFrom.Control) => {
        this.subWindowFormMethods.rooms.openEditSubWindow(roomId, path);
    };

    @mobx.action
    public handleEditAttendeeClick = (
        attendeeId: number,
        openFrom: OpenFrom = OpenFrom.Control,
        saveCompletedCallback?: (attendee: IAttendeeDetailsDTO) => void
    ) => {
        this.subWindowFormMethods.attendees.openEditSubWindow(attendeeId, openFrom, saveCompletedCallback);
    };

    private scrollToRef = (ref: React.RefObject<HTMLElement>) => {
        window.scrollTo({
            top: ref.current.offsetTop + SCROLL_HEADER_HEIGHT,
            behavior: 'smooth'
        });
    };

    public getCurrentScrollMeetingGroup = (): { date: Date; index: number } => {
        const aboveGroupRefs = this.filteredMeetingGroupRefs.filter(el => (el.ref.current?.offsetTop + SCROLL_HEADER_HEIGHT / 2) <= window.scrollY);
        const currentGroupElement = aboveGroupRefs.length ? aboveGroupRefs[aboveGroupRefs.length - 1] : this.filteredMeetingGroupRefs[0];

        const hoursArray: Date[] = this.filteredMeetingGroupRefs.map(el => el.startDateTime);
        const currentScrollMeetingGroup = hoursArray.find(hour => hour === currentGroupElement?.startDateTime);

        return { date: currentScrollMeetingGroup, index: hoursArray.indexOf(currentScrollMeetingGroup) };
    };

    public getOngoingMeetingGroup = () => {
        const { timeZone } = this.event;
        const currentTime = DateTimeFormatter.applyTimeZoneToDate(new Date(), timeZone);

        return this.filteredMeetingGroupRefs.find(({ startDateTime, endDateTime }) => {
            const startTime = DateTimeFormatter.applyTimeZoneToDate(startDateTime, timeZone);
            const endTime = DateTimeFormatter.applyTimeZoneToDate(endDateTime, timeZone);

            return currentTime.getTime() > startTime.getTime() && currentTime.getTime() < endTime.getTime();
        });
    };

    public scrollToGroupElement = (action: SelectionAction) => {
        const currentIndex = this.getCurrentScrollMeetingGroup().index;

        if (action === SelectionAction.Previous && currentIndex > 0) {
            this.scrollToRef(this.filteredMeetingGroupRefs[currentIndex - 1].ref);
        } else if (action === SelectionAction.Next && currentIndex < this.filteredMeetingGroupRefs.length - 1) {
            this.scrollToRef(this.filteredMeetingGroupRefs[currentIndex + 1].ref);
        } else if (action === SelectionAction.Now) {
            const currentTimeGroup = this.getOngoingMeetingGroup();
            if (currentTimeGroup) this.scrollToRef(currentTimeGroup.ref);
        }
    };

    @mobx.action
    public handleRunClick = async (meetingId: number) => {
        const existingSubWindowForThisMeeting = this.meetingIdToSubWindowMap[meetingId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisMeeting && existingSubWindowForThisMeeting.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisMeeting.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            if (!this.meetingsList.length) {
                await this.fetchMeetingsRequest();
            }
            const controlFormVM = IoC.Container.get<ControlFormVM>(ControlFormVM);
            let additionalTitle = this.event.oeNumber ? ' - ' + this.event.oeNumber : '';
            additionalTitle += ' - ' + this.event.name;

            controlFormVM.initialize({
                intl: this.intl,
                subWindowFormMethods: this.subWindowFormMethods,
                accountId: this.accountId,
                eventId: this.event.eventId,
                meetingId: meetingId,
                eventTimezone: this.event.timeZone,
                handleEditRoomClick: this.handleEditRoomClick,
                handleCloseSubWindow: this.closeSubWindow,
                handleEditAttendeeClick: this.handleEditAttendeeClick,
                updateMeetingsFullData: this.updateMeetingsFullData,
                meetingRunTime: this.getMeetingRunTimeInMilliseconds(meetingId),
                showEventNotesPopup: this.showEventNotesPopup,
                showConfigurePassportForm: this.showConfigurePassportForm,
                isEventNotesExist: !!this.event.eventNotes,
                guidelinesFormSubWindow: this.guidelinesFormSubWindow,
                handleTroubleshoot: (connection: IConnectionDetailsDTO) => {
                    this.handleTroubleshoot(connection, OpenFrom.RunMeeting);
                },
                trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                    this.trackHostJoinClick(eventName, OpenFrom.RunMeeting);
                },
                getPodiumLink: this.getPodiumLink,
                handlePodiumLinkFailure: this.handlePodiumLinkFailure,
                trackCopyPodiumLinkAction: this.trackCopyPodiumLinkAction,
            });
            controlFormVM.initZoomMeetingVM();
            controlFormVM.initWebinarMeetingVM();
            controlFormVM.initWebinarReportsVM();

            const currentMeeting = this._meetingsList.find(m => m.meetingId === meetingId);

            this.meetingIdToSubWindowMap[meetingId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage({
                    id: 'oe.meeting.runMeeting',
                    defaultMessage: 'Run Meeting',
                    description: 'Label text'
                }),
                additionalTitle: additionalTitle,
                className: 'oe-popup-layout--positioned',
                withExpand: true,
                componentToHost: ControlForm,
                componentToHostProps: {
                    vm: controlFormVM,
                    setMeetingStartDate: this.connectionViewModel?.getKnovioLiveStreamModalVM()?.setMeetingStartDate.bind(
                        this.connectionViewModel?.getKnovioLiveStreamModalVM(),
                        currentMeeting.startDateTime
                    )
                }
            });
        }
    };


    @mobx.action
    public openAttendeesLivePopup = async (props: IControlListItemProps) => {
        const existingSubWindowForThisAttendee = this.attendeesLiveIdToSubWindowMap[props.meetingId];
        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisAttendee && existingSubWindowForThisAttendee.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindowForThisAttendee.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.attendeesLiveIdToSubWindowMap[props.meetingId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(PARTICIPANTS_TEXT),
                componentToHost: AttendeesLiveForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.event.eventId,
                    meetingId: props.meetingId,
                    presenters: props.presenters,
                    startDateTime: props.startDateTime,
                    endDateTime: props.endDateTime,
                    total: props.total,
                    eventTimezone: props.eventTimezone,
                    registrantsCount: props.registrantsCount,
                    isTypesForMeetingsWithCounters: props.isTypesForMeetingsWithCounters,
                    attendeeDetailsPopoverVM: this.attendeeDetailsPopoverVM
                },
            });
        }

    };

    public isParticipantsFormShown = (meetingId: number): boolean => {
        return this.attendeesLiveIdToSubWindowMap[meetingId]
            && this.attendeesLiveIdToSubWindowMap[meetingId].getStatus() !== SubWindowStatus.Closed;
    };

    private syncMeeting = (meetingId: number) => {
        this.meetingsForSync.push(meetingId);
        this.syncMeetingCallback();
    };

    private syncMeetings = async () => {
        const updatedMeetings: IControlMeetingDetailsDTO[] = [];
        const fetchMeetingDetailsPromises = this.meetingsForSync.map(async (meetingId: number) => {
            const meetingDetails: IControlMeetingDetailsDTO = await this.controlDAO.getControlEventMeeting({
                meetingId: meetingId,
                eventId: this.event.eventId,
                accountId: this.accountId,
                ignoreAttendees: IGNORE_ATTENDEES
            });

            updatedMeetings.push(meetingDetails);
        });

        await Promise.all(fetchMeetingDetailsPromises);

        this.updateMeetingsFullData(updatedMeetings);
        this.meetingsForSync = [];
    };

    private syncMeetingCallback = debounce(this.syncMeetings, 0);


    @mobx.action
    private updateDataInEvent(syncEventResponse: IOnSyncEventSubscriptionResponse): void {
        const { value: { data: { onSyncEvent: syncEventData } } } = syncEventResponse;

        if (syncEventData.type === OnSyncEventType.LIVE_SESSION_UPDATED) return;

        const meetingChangeset = MeetingChangesetMapper.syncedEventDataToMeetingChangeset(syncEventData);

        if (MeetingChangesetMapper.isMeetingSyncEvent(meetingChangeset.type)) {
            this.updateMeetingInEvent(meetingChangeset as MeetingDataSyncChangeset);
        } else if (MeetingChangesetMapper.isMeetingRoomSyncEvent(meetingChangeset.type)) {
            this.updateMeetingRoomInEvent(meetingChangeset as MeetingRoomDataSyncChangeset);
        } else if (MeetingChangesetMapper.isEventSyncEvent(meetingChangeset.type)) {
            this.updateMeetingsInEvent(meetingChangeset as EventDataSyncChangeset);
        }
    }

    @mobx.action
    private updateMeetingsInEvent(meetingChangeset: EventDataSyncChangeset): void {
        switch (meetingChangeset.type) {
            case OnSyncEventType.ATTENDEE_UPDATED:
                this.updateAttendeesInEvent(meetingChangeset);
                break;
            default:
                break;
        }
    }

    @mobx.action
    private updateAttendeesInEvent(meetingChangeset: EventDataSyncChangeset): void {
        this.meetingsList.forEach((meeting, meetingIndex) => {
            const updatedOrganizations = meetingChangeset.applyChangesToMeeting({
                participants: meeting.participants,
                presenters: meeting.presenters,
                hosts: meeting.hosts,
                supports: meeting.supports,
            });

            const [orgRole] = Object.keys(updatedOrganizations);

            if (orgRole) this.meetingsList[meetingIndex][orgRole] = updatedOrganizations[orgRole];
        });
    }

    @mobx.action
    private updateMeetingInEvent(meetingChangeset: MeetingDataSyncChangeset): void {
        const meetingId = meetingChangeset.getMeetingId();
        const meetingIndex = this.getMeetingIndexById(meetingId);

        if (meetingIndex === -1) return;

        const _meeting = meetingChangeset.applyChangesToMeeting({ ...this.meetingsList[meetingIndex] });

        if (meetingChangeset.type === OnSyncEventType.MEETING_ATTENDEE_UPDATED) {
            const [ orgRole ] = Object.keys(_meeting);
            if (orgRole) this.meetingsList[meetingIndex][orgRole] = _meeting[orgRole];
        } else {
            this.meetingsList[meetingIndex] = _meeting as IControlMeetingDetailsDTO;
        }

        this.setGroupedOperators(this.meetingsList[meetingIndex]);
    }

    @mobx.action
    private updateMeetingRoomInEvent(meetingChangeset: MeetingRoomDataSyncChangeset): void {
        const meetingRoomId = meetingChangeset.getMeetingRoomId();
        this.meetingsList.forEach((meeting, idx) => {
            if (meeting.room && meeting.room.roomId === meetingRoomId) {
                this.meetingsList[idx] = meetingChangeset.applyChangesToMeeting({ ...meeting }) as IControlMeetingDetailsDTO;
                this.setGroupedOperators(this.meetingsList[idx]);
            }
        });
    }

    public async subscribeToEventUpdates(): Promise<void> {
        this.eventDataSubscription = await subscribeAppsyncData<IOnSyncEventSubscriptionResponse>(onSyncEvent, { eventId: this.event.eventId },
            data => this.updateDataInEvent(data),
            () => this.subscribeToEventUpdates()
        );
    }

    public unsubscribeFromEventUpdates(): void {
        this.eventDataSubscription?.unsubscribe();
    }

    public getMeetingIndexById(meetingId: number): number {
        return this.meetingsList.findIndex(meeting => meeting.meetingId === meetingId);
    }

    public subscribeMeeting = async (meetingId: number, roomId?: number): Promise<IAppsyncSubscription[]> => {
        this.syncMeeting(meetingId);
        const subscriptions: IAppsyncSubscription[] = [];

        const meetingSubscription = await subscribeAppsyncData<IUpdateMeetingSubscriptionResponse>(onUpdateMeetingControl, { meetingId: meetingId },
            data => {
                const { value: { data: { onUpdateMeetingControl: updatedMeetingData } } } = data;
                this.updateMeetingData(updatedMeetingData);
            },
            () => this.subscribeMeeting(meetingId, roomId)
        );
        subscriptions.push(meetingSubscription);

        const attendeeLiveDataSubscription = await subscribeAppsyncData<IUpdateMeetingAttendeeLiveDataResponse>(
            onPopulateAggregatedAttendeeLiveCounters,
            { meetingId: meetingId },
            data => {
                const {value: {data: {onPopulateAggregatedAttendeeLiveCounters: liveData}}} = data;
                this.updateMeetingLiveData(liveData);
            }
        );
        subscriptions.push(attendeeLiveDataSubscription);

        if (roomId) {
            const roomSubscription = await subscribeAppsyncData<IUpdateRoomSubscriptionResponse>(onUpdateRoom, { roomId: roomId },
                data => {
                    const { value: { data: { onUpdateRoom: updatedRoomData } } } = data;
                    this.updateMeetingRoomData(meetingId, updatedRoomData);
                },
            );
            subscriptions.push(roomSubscription);
        }

        return subscriptions;
    };

    public unsubscribeMeeting = subscriptions => {
        subscriptions.forEach(el => {
            el?.unsubscribe();
        });
    };

    @mobx.action
    public updateMeetingsFullData = (updatedMeetings: IControlMeetingDetailsDTO[]) => {
        updatedMeetings.forEach(meeting => {
            const prevMeeting = this.meetingsList.find(({ meetingId: id }) => id === meeting.meetingId);
            const meetingIndex = this.meetingsList.indexOf(prevMeeting);

            if (meetingIndex !== -1) {
                this.meetingsList[meetingIndex] = {
                    ...meeting,
                    presenters: meeting.presenters.map(newPresenter => {
                        const oldPresenter = prevMeeting.presenters.find(p => {
                            return p.organizationId.toString(10) === newPresenter.organizationId.toString(10);
                        });
                        const oldPresenterStillHaveFirstMeetingInTheDay = oldPresenter && !newPresenter.attendees.some(attendee => {
                            return attendee.status === MeetingAttendeeStatus.JOINED || attendee.status === MeetingAttendeeStatus.WALK_IN;
                        });
                        if (oldPresenter && oldPresenterStillHaveFirstMeetingInTheDay && meeting.status !== MeetingStatus.Cancelled) {
                            return {
                                ...newPresenter,
                                firstMeetingInTheDay: oldPresenter.firstMeetingInTheDay
                            };
                        }
                        return newPresenter;
                    })
                };
                this.setGroupedOperators(this.meetingsList[meetingIndex]);
            }
        });
    };

    @mobx.action
    private updateMeetingData = (updatedMeeting: IControlMeetingDetailsDTO) => {
        const prevMeeting = this.meetingsList.find(({ meetingId }) =>
            meetingId === Number(updatedMeeting.meetingId)
        );

        const meetingIndex = this.meetingsList.indexOf(prevMeeting);
        if (meetingIndex !== -1) {
            this.meetingsList[meetingIndex] = {
                ...prevMeeting,
                participants: updatedMeeting.participants,
                presenters: this.mapPresentersWithFirstMeetingInTheDay(
                    updatedMeeting.presenters,
                    prevMeeting.presenters,
                    prevMeeting.status
                ),
                hosts: updatedMeeting.hosts,
                connections: this.getMappedAndSortedConnections(
                    updatedMeeting.connections,
                    prevMeeting.connections
                ),
                topic: updatedMeeting.topic,
                startDateTime: new Date(updatedMeeting.startDateTime)
            };
        }
    };

    @mobx.action
    private updateMeetingRoomData = (meetingId: number, updatedRoom: IControlRoomDTO) => {
        const prevMeeting = this.meetingsList.find(({ meetingId: id }) => id === meetingId);

        const meetingIndex = this.meetingsList.indexOf(prevMeeting);
        if (meetingIndex !== -1) {
            this.meetingsList[meetingIndex] = {
                ...prevMeeting,
                room: {
                    ...prevMeeting.room,
                    roomId: Number(updatedRoom.roomId),
                    connections: updatedRoom.connections
                        .map(connection => getComposedConnectionValues(prevMeeting.room.connections, connection))
                        .sort((a, b) => a.type.localeCompare(b.type))
                }
            };
        }
    };

    @mobx.action
    private updateMeetingLiveData = (liveData: IMeetingAttendeeLiveData) => {
        const prevMeeting = this.meetingsList.find(el => el.meetingId === liveData.meetingId);
        const meetingIndex = this.meetingsList.indexOf(prevMeeting);

        if (meetingIndex !== -1) {
            this.meetingsList[meetingIndex] = {
                ...prevMeeting,
                total: liveData.total
            };

            this.setGroupedOperators(this.meetingsList[meetingIndex]);
        }
    };

    private mapPresentersWithFirstMeetingInTheDay(
        updatedPresenters: IAttendeeOrganizationDTO[],
        prevPresenters: IAttendeeOrganizationDTO[],
        prevMeetingStatus: MeetingStatus
    ) {
        return updatedPresenters.map(newPresenter => {
            const oldPresenter = prevPresenters.find(p =>
                p.organizationId.toString(10) === newPresenter.organizationId.toString(10)
            );
            const oldPresenterStillHaveFirstMeetingInTheDay =
                oldPresenter &&
                !newPresenter.attendees.some(attendee =>
                    attendee.status === MeetingAttendeeStatus.JOINED ||
                    attendee.status === MeetingAttendeeStatus.WALK_IN
                );
            if (oldPresenter && oldPresenterStillHaveFirstMeetingInTheDay && prevMeetingStatus !== MeetingStatus.Cancelled) {
                return {
                    ...newPresenter,
                    firstMeetingInTheDay: oldPresenter.firstMeetingInTheDay
                };
            }
            return newPresenter;
        });
    };

    private getMappedAndSortedConnections(updatedConnections: IConnectionDetailsDTO[], prevConnections: IConnectionDetailsDTO[]) {
        return updatedConnections
            .map(connection => getComposedConnectionValues(prevConnections, connection))
            .sort((a, b) => a.type.localeCompare(b.type));
    };

    public closeSubWindow = meetingId => {
        if (meetingId) {
            this.meetingIdToSubWindowMap[meetingId].close();
        }
    };

    public isMeetingRunFormShown = (meetingId: number) => {
        return this.meetingIdToSubWindowMap[meetingId]
            && this.meetingIdToSubWindowMap[meetingId].getStatus() !== SubWindowStatus.Closed;
    };

    public getMeetingRunTimeInMilliseconds = (meetingId: number) => {
        const meeting = this.meetingsList.find((el: IControlMeetingDetailsDTO) => el.meetingId === meetingId);
        return new Date().getTime() - meeting.startDateTime.getTime();
    };

    public getAccountId = () => {
        return this.accountId;
    };

    public getEvent = () => {
        return this.event as Readonly<IEventDetailsDTO>;
    };

    public getMeetingsList = () => {
        return this.meetingsList;
    };

    public isLoading = () => {
        return this.loading;
    };

    @mobx.action
    public onCollapseMeetings = (meetingIds: number[]) => {
        this.collapsedMeetingIds = this.collapsedMeetingIds.concat(meetingIds);
    };

    @mobx.action
    public onExpandMeetings = (meetingIds: number[]) => {
        this.collapsedMeetingIds = this.collapsedMeetingIds.filter(el => {
            return !meetingIds.includes(el);
        });
    };

    @mobx.action
    public onCollapseGroup = (group: IMeetingTimeGroup, hidden = false) => {
        if (hidden) {
            this.collapsedHiddenGroups = [...this.collapsedHiddenGroups, group];
        } else {
            this.collapsedGroups = [...this.collapsedGroups, group];
        }
    };

    @mobx.action
    public onExpandGroup = (group: IMeetingTimeGroup, hidden = false) => {
        const { startDateTime, endDateTime } = group;
        if (hidden) {
            this.collapsedHiddenGroups = this.collapsedHiddenGroups.filter(
                el => el.startDateTime !== startDateTime && el.endDateTime !== endDateTime
            );
        } else {
            this.collapsedGroups = this.collapsedGroups.filter(
                el => el.startDateTime !== startDateTime && el.endDateTime !== endDateTime
            );
        }
    };

    @mobx.action
    private handleMeetingsLoaded = (eventMeetingsDTO: IControlEventMeetingsDTO) => {
        this.meetingsList = eventMeetingsDTO.content;
        this.loading = false;
        this.paginationVM.setTotalRecords(eventMeetingsDTO.totalElements);
        this.collapseHiddenSections();
    };

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    public getPaginationVM() {
        return this.paginationVM;
    }

    @mobx.action
    private setUpPagination() {
        this.paginationVM = new PagingViewModel({ withChooser: true, pageSizeStorageName: CONTROL_PAGE_SIZE_NAME });
        mobx.reaction(() => [this.paginationVM.getCurrentPage(), this.paginationVM.getPageSize()],
            () => this.fetchMeetingsRequest());
    }

    public handleConnectionChange = async (meetingId: number, trackEvent?: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
        const meetingDetails: IControlMeetingDetailsDTO = await this.controlDAO.getControlEventMeeting({
            meetingId: meetingId,
            eventId: this.event.eventId,
            accountId: this.accountId,
            ignoreAttendees: IGNORE_ATTENDEES
        });
        this.updateMeetingsFullData([meetingDetails]);

        if (trackEvent) {
            this.trackEventMixPanel(TRACKING_EVENTS.CONTROL_PAGE_CONNECTION_DETAILS_POPOVER, trackEvent);
        }
    };

    public openPopupEditConnection = (connection: IConnectionDetailsDTO, meetingId: number, roomId?: number) => {
        const isEdit = this.zoomMeetingVM.getEditModeStatus();
        if (connection.type === ConnectionType.Zoom && !isEdit) {
            this.zoomMeetingVM.getZoomDetails(connection);
            const callBack = roomId
                ? (c: IConnectionDetailsDTO) => this.onEditRoomConnection(c, meetingId, roomId, false)
                : (c: IConnectionDetailsDTO) => this.onEditMeetingConnection(c, meetingId, false);
            this.zoomMeetingVM.setUpdateZoomMeetingCallback((data: IConnectionDetailsDTO) => {
                this.updateZoomMeetingCallback(
                    callBack,
                    // manually assign blueprintId to the connection fetched from the zoom
                    { ...data, blueprintId: connection.blueprintId }
                );
            });
            return;
        }

        if (connection.type === ConnectionType.Webinar && !this.webinarMeetingVM.isEditMode) {
            const callBack = roomId
                ? (c: IConnectionDetailsDTO) => this.onEditRoomConnection(c, meetingId, roomId, false)
                : (c: IConnectionDetailsDTO) => this.onEditMeetingConnection(c, meetingId, false);

            this.webinarMeetingVM.openEditWebinarModal(connection);
            this.webinarMeetingVM.setUpdateWebinarMeetingCallback((data: IConnectionDetailsDTO) => {
                this.updateWebinarMeetingCallback(callBack, data);
            });
            return;
        }

        if (this.connectionViewModel.getKnovioLiveStreamModalVM() && connection.type === ConnectionType.Knovio) {
            const currentMeeting = this._meetingsList.find(m => m.meetingId === meetingId);
            this.connectionViewModel.getKnovioLiveStreamModalVM().setMeetingStartDate(currentMeeting.startDateTime);
        }

        if (roomId) {
            return this.onEditRoomConnection(connection, meetingId, roomId);
        }
        return this.onEditMeetingConnection(connection, meetingId);
    };

    public onEditMeetingConnection = (connection: IConnectionDetailsDTO, meetingId: number, shouldSetModalCallback?: boolean) => {
        let shouldShowKnovioReplayToggle = false;
        let organizationsForSpeakersSync = null;

        if (connection.type === ConnectionType.Knovio || connection.type === ConnectionType.Other) {
            const currentMeeting = this._meetingsList.find(m => m.meetingId === meetingId);
            const knovioReplayConnection = currentMeeting.connections.find(c => c.passportReplayEnabled);
            shouldShowKnovioReplayToggle =
                !knovioReplayConnection
                || connection.meetingConnectionId === knovioReplayConnection.meetingConnectionId
            ;
            organizationsForSpeakersSync = getOrganizationsForSpeakersSync(currentMeeting);
        }


        this.subWindowFormMethods.meetingConnectionDetails.openEditSubWindow(meetingId, connection, () =>
            this.handleConnectionChange(meetingId, CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.EDIT_CONNECTION), shouldSetModalCallback,
            shouldShowKnovioReplayToggle,
            { organizationsForSpeakersSync: organizationsForSpeakersSync }
        );
    };
    public onEditRoomConnection = (connection: IConnectionDetailsDTO, meetingId: number, roomId: number, shouldSetModalCallback?: boolean) => {
        this.subWindowFormMethods.roomConnectionDetails.openEditSubWindow(roomId, connection, () =>
            this.handleConnectionChange(meetingId, CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.EDIT_CONNECTION), shouldSetModalCallback
        );
    };
    public onDeleteMeetingConnection = (
        data: IDeleteConnectionRequestParams,
        meetingId: number,
        connectionDetails: IDeleteConnectionDetails
    ) => {
        this.subWindowFormMethods.meetingConnectionDetails.openDeleteSubWindow(meetingId, data, () =>
            this.handleConnectionChange(meetingId, CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.DELETE_CONNECTION), connectionDetails
        );
    };

    public onDeleteRoomConnection = (
        data: IDeleteConnectionRequestParams,
        meetingId: number,
        roomId: number,
        connectionDetails: IDeleteConnectionDetails
    ) => {
        this.subWindowFormMethods.roomConnectionDetails.openDeleteSubWindow(roomId, data, () =>
            this.handleConnectionChange(meetingId, CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.DELETE_CONNECTION), connectionDetails
        );
    };

    public setFetchCustomFields = (fetchCustomFields: () => Promise<void>) => {
        this.fetchCustomFields = fetchCustomFields;
    };

    public setGenerateMeetingInteractionsReport = (
        generateMeetingInteractionsReport: (
            meetingId:number,
            reportGenerationNotificationProps: IReportGenerationNotificationProps,
        ) => Promise<void>
    ) => {
        this.generateMeetingInteractionsReport = generateMeetingInteractionsReport;
    };

    public setGenerateMeetingRegistrationReport = (
        generateMeetingRegistrationReport: (
            meetingId: number,
            reportGenerationNotificationProps: IReportGenerationNotificationProps,
        ) => Promise<void>
    ) => {
        this.generateMeetingRegistrationReport = generateMeetingRegistrationReport;
    };

    @mobx.action
    public setGroupedOperators = (meeting: IControlMeetingDetailsDTO) => {
        const operators = [...(meeting.room?.operators || []), ...(meeting.operators || [])];
        const sortedOperators = operators.slice().sort((a, b) => a.lastName.localeCompare(b.lastName));
        const groupedOperators: { [path: string]: IOperatorDetailsDTO[] } = {};

        OPERATOR_ROLES.forEach(key => groupedOperators[key] = []);
        sortedOperators.forEach(operator => groupedOperators[operator.operatorRole]?.push(operator));

        this.groupedOperatorsMap[meeting.meetingId] = groupedOperators;
    };

    public viewEventGuidelines = () => {
        this.showEventNotesPopup(false, OpenFrom.Control);
        this.trackEventMixPanel(TRACKING_EVENTS.CONTROL_PAGE_ACTIONS, CONTROL_PAGE_TRACKING_EVENTS.VIEW_GUIDELINES);
    };

    public editEventGuidelines = () => {
        this.showEventNotesPopup(true, OpenFrom.Control);
    };

    public trackEventMixPanel = (eventGroup: TRACKING_EVENTS, eventName: string, openFrom: OpenFrom = OpenFrom.Control): void => {
        this.trackingService.get().track(eventGroup, {
            [eventName]: openFrom
        });
    };

    public trackFiltersEventMixPanel = (): void => {
        this.trackingService.get().track(TRACKING_EVENTS.CONTROL_PAGE_FILTERS, {
            'Date': convertBoolTypeForMixPanel(this.hasSelectedDateRange()),
            'Status': convertBoolTypeForMixPanel(!!this.selectedFilterStatuses),
            'Meeting Type': convertBoolTypeForMixPanel(!!this.selectedFilterTypes),
            'Operators': convertBoolTypeForMixPanel(!!this.selectedFilterOperators),
            'Chaperone': convertBoolTypeForMixPanel(!!this.selectedChaperone),
            'Search': convertBoolTypeForMixPanel(!!this.searchQuery)
        });
    };

    private setDefaultColumns = (): void => {
        this.defaultColumns = [
            {
                id: 'status', label: this.intl.formatMessage({
                    id: 'oe.common.status',
                    defaultMessage: 'Status',
                    description: 'Label text'
                })
            },
            {
                id: 'presenters', label: this.intl.formatMessage({
                    id: 'oe.common.presenters',
                    defaultMessage: 'Presenters',
                    description: 'Label text'
                })
            },
            {
                id: 'participants', label: this.intl.formatMessage({
                    id: 'oe.common.participants',
                    defaultMessage: 'Participants',
                    description: 'Label text'
                })
            },
            {
                id: 'hosts', label:
                    this.intl.formatMessage({
                        id: 'oe.common.hosts',
                        defaultMessage: 'Hosts',
                        description: 'Label text'
                    })
            },
            {
                id: 'type', label: this.intl.formatMessage(TYPE)
            },
            {
                id: 'operators', label: this.intl.formatMessage({
                    id: 'oe.common.operators',
                    defaultMessage: 'Operators',
                    description: 'Label text'
                })
            },
            {
                id: 'chaperone', label: this.intl.formatMessage({
                    id: 'oe.common.chaperone',
                    defaultMessage: 'Chaperone',
                    description: 'Label text'
                })
            },
            {
                id: 'connections', label: this.intl.formatMessage({
                    id: 'oe.common.connections',
                    defaultMessage: 'Connections',
                    description: 'Label text'
                })
            },
        ].filter(Boolean);
        this.selectedFilterColumns = this.defaultColumns;
    };


    @mobx.action
    public handleChangeControlFilterColumns = (eventColumnsItems: IFilterValue[]): void => {
        this.selectedFilterColumns = eventColumnsItems
            .slice()
            .sort((a, b) => {
                const aIndex = this.defaultColumns.findIndex(c => c.id === a.id);
                const bIndex = this.defaultColumns.findIndex(c => c.id === b.id);
                return aIndex - bIndex;
            });
        this.addFiltersToStorage();
    };


    @mobx.action
    public selectFilters = (filters: ISelectedFilter[]): void => {
        this.selectedFilterColumns = filters.find(el => el.filterName === 'Columns')?.filterValues || this.defaultColumns;
        this.addFiltersToStorage();
    };

    public loadFiltersFromStorage = (): void => {
        const filtersString = localStorage.getItem(this.localStorageFiltersName);
        try {
            const filters = JSON.parse(filtersString);
            this.selectFilters(filters);
        } catch {
          this.selectedFilterColumns = this.defaultColumns;
          this.addFiltersToStorage();
        }
    };

    public getSelectedFilters = (): ISelectedFilter[] => {
        return [
            {
                filterName: 'Columns',
                filterValues: this.selectedFilterColumns
            }
        ].filter(Boolean);
    };

    public addFiltersToStorage = (): void => {
        const selectedFilters = this.getSelectedFilters();
        localStorage.setItem(this.localStorageFiltersName, JSON.stringify(selectedFilters));
    };

    public getSelectedFilterArray = (selectedFilter: string): string[] => {
        return selectedFilter.split(',').filter(val => val);
    };

    private saveSelectedFilters = (): void => {
        try {
            const selectedFilters = this.getSelectedTitleFilters();
            const currentFilters: TSavedMeetingsFilterItem = JSON.parse(localStorage.getItem(CONTROL_TAB_SELECTED_FILTERS)) ?? {};
            currentFilters[this.event.eventId] = selectedFilters;
            localStorage.setItem(CONTROL_TAB_SELECTED_FILTERS, JSON.stringify(currentFilters));
        } catch (error) {
            localStorage.removeItem(CONTROL_TAB_SELECTED_FILTERS);
        }
    };

    public getSelectedTitleFilters = (): TMeetingsFilterItem[] => {
        return [
            this.selectedFilterTypes.length && {
                filterName: 'selectedFilterTypes',
                filterValues: this.selectedFilterTypes
            },
            this.selectedFilterOperators.length && {
                filterName: 'selectedFilterOperators',
                filterValues: this.selectedFilterOperators
            },
            this.selectedChaperone.length && {
                filterName: 'selectedChaperone',
                filterValues: this.selectedChaperone
            },
            this.selectedFilterStatuses.length && {
                filterName: 'selectedStatuses',
                filterValues: this.selectedFilterStatuses
            },
            this.hasSelectedDateRange() && {
                filterName: 'selectedFilterHours',
                filterValues: this.selectedFilterDays.map(d => d.toISOString()),
            },
            {
                filterName: 'selectedSortOrder',
                filterValues: this.selectedSortOrder
            },
        ].filter(Boolean) as TMeetingsFilterItem[];
    };

    @mobx.action
    public loadSelectedFilters = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.loading = true;
            });

            const filterStringValue = localStorage.getItem(CONTROL_TAB_SELECTED_FILTERS);
            const controlTabFilters: TSavedMeetingsFilterItem = JSON.parse(filterStringValue);
            if (!controlTabFilters) return;

            const savedEventFilters = controlTabFilters[this.event.eventId];
            if (!savedEventFilters) return;

            await this.compareFilters(savedEventFilters);
            this.applySavedFilters(savedEventFilters);
        } catch (error) {
            ControlViewModel.handleApiException(error);
        }
    };

    @mobx.action
    private applySavedFilters = (filters: TMeetingsFilterItem[]): void => {
        if (filters) {
            filters.forEach(({ filterName, filterValues}) => {
                switch (filterName) {
                    case 'selectedFilterHours':
                        this.selectedFilterDays = filterValues.map(d => new Date(d));
                        break;
                    case 'selectedStatuses':
                        this.selectedFilterStatuses = filterValues;
                        break;
                    default:
                        // @ts-ignore
                        this[filterName] = filterValues;
                }
            });
        }
    };

    private compareFilters = async (savedEventFilters: TMeetingsFilterItem[]) => {
        try {
            const [
                meetingsTypes,
                meetingsStatuses,
                operatorsList,
                chaperonesList
            ] = await Promise.all([
                this.fetchMeetingTypes(),
                this.getMeetingStatuses(),
                this.fetchOperators(),
                this.fetchChaperone(),
                this.fetchFilterHours()
            ]);
            this.compareSavedTypesWithResultTypes(
                savedEventFilters.find(f => f.filterName === 'selectedFilterTypes') as TSelectedFilterTypes,
                meetingsTypes
            );
            this.compareSavedStatusesWithResultStatuses(
                savedEventFilters.find(f => f.filterName === 'selectedStatuses') as TSelectedFilterStatuses,
                meetingsStatuses
            );
            this.compareSavedOperatorsWithResultOperators(
                savedEventFilters.find(f => f.filterName === 'selectedFilterOperators') as TSelectedFilterOperators,
                operatorsList
            );
            this.compareSavedChaperonesWithResultChaperones(
                savedEventFilters.find(f => f.filterName === 'selectedChaperone') as TSelectedFilterChaperone,
                chaperonesList
            );

        } catch (error) {
            ControlViewModel.handleApiException(error);
        }
    };

    private compareSavedTypesWithResultTypes = (
        savedSelectedFilterTypes: TSelectedFilterTypes | undefined,
        meetingsTypes: { id: string; label: string }[]
    ): void => {
        if (!savedSelectedFilterTypes) return;

        savedSelectedFilterTypes.filterValues = savedSelectedFilterTypes.filterValues
            .split(',')
            .filter(savedType => meetingsTypes.find(({ id }) => id === savedType))
            .join(',')
        ;
    };

    private compareSavedStatusesWithResultStatuses = (
        selectedFilterStatuses: TSelectedFilterStatuses | undefined,
        meetingsStatuses: { id: string; label: string }[]
    ): void => {
        if (!selectedFilterStatuses) return;

        selectedFilterStatuses.filterValues = selectedFilterStatuses.filterValues
            .split(',')
            .filter(savedStatus => meetingsStatuses.find(({ id }) => id === savedStatus))
            .join(',')
        ;
    };

    private compareSavedOperatorsWithResultOperators = (
        selectedFilterOperators: TSelectedFilterOperators | undefined,
        operatorsList: { id: string; label: string }[]
    ): void => {
        if (!selectedFilterOperators) return;

        selectedFilterOperators.filterValues = selectedFilterOperators.filterValues
            .split(',')
            .filter(savedId => operatorsList.find(({ id }) => id === savedId))
            .join(',')
        ;
    };

    private compareSavedChaperonesWithResultChaperones = (
        selectedFilterChaperons: TSelectedFilterChaperone | undefined,
        chaperonesList: { id: string; label: string }[]
    ): void => {
        if (!selectedFilterChaperons) return;

        selectedFilterChaperons.filterValues = selectedFilterChaperons.filterValues
            .split(',')
            .filter(chaperon => chaperonesList.find(({ id }) => id === chaperon))
            .join(',')
        ;
    };

    private trackHandlerInAttendeePopover = (eventName: ATTENDEE_INFO_POPOVER_TRACKING_EVENTS, openFrom?: OpenFrom): void => {
        this.trackEventMixPanel(TRACKING_EVENTS.COPY_ATTENDEE_NAME_FROM_POPOVER, eventName, openFrom);
    };

    public shouldDisableDeleteButton = (connection: IConnectionDetailsDTO): boolean => {
        return connection?.autoSyncEnabled && !this.executionRuntime.isReady();
    };

    public getPodiumLink = async (params: MeetingApiParams): Promise<string> => {
        const podiumLintDTO = await this.podiumDao.getPodiumLink(params);
        return podiumLintDTO.podiumLink;
    };

    public handlePodiumLinkFailure(e: Error) {
        ControlViewModel.handleApiException(e);
    }

    public trackCopyPodiumLinkAction = (from: TCopyPodiumLinkFrom, meetingId: number): void => {
        this.trackingService.get().track(
            'Copy Podium Link',
            {
                'From': from,
                'Event Id': this.event.eventId,
                'Meeting Id': meetingId,
            }
        );
    };
}

export default ControlViewModel;
